import {
  MAX_OPTIONS,
  MAX_QUESTIONS,
  QUESTION_TYPES,
  type QuestionType,
  type Survey,
  type SurveyQuestion,
  type SurveyStatus,
} from '@sse/schema';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Download,
  ExternalLink,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Card, Field, btnGhost, btnPrimary, btnSecondary, inputCls } from '../components/ui';
import { ApiError } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';
import { SurveyBadge } from './surveys';

const TYPE_LABELS: Record<QuestionType, string> = {
  single: 'Una scelta',
  multiple: 'Più scelte',
  rating: 'Voto da 1 a 5',
  text: 'Testo libero',
};

interface QuestionResult {
  id: string;
  answered: number;
  counts?: Record<string, number>;
  ratings?: number[];
  average?: number;
  texts?: { text: string; at: string }[];
}
interface Results {
  total: number;
  questions: QuestionResult[];
}

/** Domanda nel form: le risposte possibili come testo, una per riga. */
interface DraftQuestion {
  id: string;
  type: QuestionType;
  label: string;
  hint: string;
  options: string;
  required: boolean;
}
interface Draft {
  title: string;
  intro: string;
  endsOn: string;
  questions: DraftQuestion[];
}

const newId = () => `q${Math.random().toString(36).slice(2, 8)}`;
const blankQuestion = (type: QuestionType = 'single'): DraftQuestion => ({
  id: newId(),
  type,
  label: '',
  hint: '',
  options: '',
  required: true,
});
const toDraft = (s?: Survey): Draft => ({
  title: s?.title ?? '',
  intro: s?.intro ?? '',
  endsOn: s?.endsOn ?? '',
  questions: s?.questions.map((q) => ({
    id: q.id,
    type: q.type,
    label: q.label,
    hint: q.hint ?? '',
    options: (q.options ?? []).join('\n'),
    required: q.required,
  })) ?? [blankQuestion()],
});
const lines = (s: string) =>
  s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);
const toBody = (d: Draft) => ({
  title: d.title,
  intro: d.intro,
  ...(d.endsOn ? { endsOn: d.endsOn } : {}),
  questions: d.questions.map((q): SurveyQuestion => ({
    id: q.id,
    type: q.type,
    label: q.label,
    ...(q.hint.trim() ? { hint: q.hint.trim() } : {}),
    ...(q.type === 'single' || q.type === 'multiple' ? { options: lines(q.options) } : {}),
    required: q.required,
  })),
});

const errorText = (e: unknown) => {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : String(e);
  const issues = e.body.issues?.map((i) => `${i.path}: ${i.message}`).join('; ');
  return issues ? `${e.message} (${issues})` : e.message;
};

/**
 * Scheda di un sondaggio (solo admin, docs/07-admin-e-bot.md "Sondaggi"): testi e domande,
 * pubblicazione e chiusura, risultati e CSV. Con risposte già arrivate si cambiano solo i testi.
 */
export function SurveyEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { api, isAdmin, config, accessToken } = useSession();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [results, setResults] = useState<Results | null>(null);
  const [draft, setDraft] = useState<Draft>(toDraft());
  const [saved, setSaved] = useState(JSON.stringify(toDraft()));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);

  const load = async () => {
    if (isNew) return;
    try {
      const r = await api<{ survey: Survey; results: Results }>(`/surveys/${id}`);
      setSurvey(r.survey);
      setResults(r.results);
      const d = toDraft(r.survey);
      setDraft(d);
      setSaved(JSON.stringify(d));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAdmin]);

  const dirty = JSON.stringify(draft) !== saved;
  const locked = (survey?.responses ?? 0) > 0;

  if (!isAdmin)
    return <main className="p-6 text-muted">I sondaggi sono riservati agli admin.</main>;
  if (loading) return <main className="p-6 text-sm text-muted">Caricamento…</main>;

  const run = async (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  /** Salva le modifiche; restituisce la versione salvata (per cambiare subito lo stato). */
  const save = async (): Promise<Survey> => {
    if (isNew) {
      const r = await api<{ survey: Survey }>('/surveys', { method: 'POST', body: toBody(draft) });
      setSaved(JSON.stringify(draft));
      navigate(`/surveys/${r.survey.id}`, { replace: true });
      return r.survey;
    }
    if (!dirty && survey) return survey;
    const r = await api<{ survey: Survey }>(`/surveys/${id}`, {
      method: 'PUT',
      body: { ...toBody(draft), version: survey!.version },
    });
    setSurvey(r.survey);
    setSaved(JSON.stringify(draft));
    return r.survey;
  };

  const setStatus = (status: SurveyStatus, message: string) =>
    run(status, async () => {
      const current = await save();
      const r = await api<{ survey: Survey }>(`/surveys/${current.id}/status`, {
        method: 'POST',
        body: { status, version: current.version },
      });
      setSurvey(r.survey);
      setNotice(message);
    });

  const remove = () =>
    run('delete', async () => {
      if (
        !window.confirm(
          `Eliminare «${survey?.title}» con tutte le ${survey?.responses ?? 0} risposte? Non si può annullare.`,
        )
      )
        return;
      await api(`/surveys/${id}`, { method: 'DELETE' });
      navigate('/surveys');
    });

  const downloadCsv = () =>
    run('csv', async () => {
      const res = await fetch(`${config.apiUrl}/v1/admin/surveys/${id}/csv`, {
        headers: { Authorization: `Bearer ${await accessToken()}` },
      });
      if (!res.ok) throw new Error(`CSV non scaricato (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = Object.assign(document.createElement('a'), {
        href: url,
        download: `sondaggio-${id}.csv`,
      });
      a.click();
      URL.revokeObjectURL(url);
    });

  const setQ = (i: number, patch: Partial<DraftQuestion>) =>
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)),
    }));
  const move = (i: number, by: -1 | 1) =>
    setDraft((d) => {
      const qs = [...d.questions];
      const [q] = qs.splice(i, 1);
      qs.splice(i + by, 0, q!);
      return { ...d, questions: qs };
    });

  const status = survey?.status ?? 'draft';
  return (
    <main className="mx-auto max-w-[1000px] p-4 md:p-6">
      <Link to="/surveys" className={btnGhost}>
        <ArrowLeft className="size-4" aria-hidden /> Sondaggi
      </Link>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="font-display text-h2">{isNew ? 'Nuovo sondaggio' : survey?.title}</h1>
        {!isNew && <SurveyBadge status={status} />}
        {survey && (
          <span className="text-sm text-muted">
            {survey.responses} {survey.responses === 1 ? 'risposta' : 'risposte'}
            {survey.publishedAt ? ` · pubblicato ${formatStamp(survey.publishedAt)}` : ''}
          </span>
        )}
      </div>

      {/* azioni */}
      <div className="sticky top-14 z-10 mt-4 flex flex-wrap items-center gap-2 border-y border-surface-strong bg-page py-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={Boolean(busy) || (!isNew && !dirty)}
          onClick={() => void run('save', async () => void (await save()))}
        >
          {busy === 'save' && <Loader2 className="size-4 animate-spin" aria-hidden />}
          {isNew ? 'Crea la bozza' : 'Salva'}
        </button>
        {status !== 'active' && (
          <button
            type="button"
            className={btnSecondary}
            disabled={Boolean(busy)}
            onClick={() =>
              void setStatus(
                'active',
                'Pubblicato: entro un minuto circa compare sul sito, in cima alle pagine e in home.',
              )
            }
          >
            {status === 'closed' ? 'Riapri sul sito' : 'Pubblica sul sito'}
          </button>
        )}
        {status === 'active' && (
          <button
            type="button"
            className={btnSecondary}
            disabled={Boolean(busy)}
            onClick={() =>
              void setStatus('closed', 'Chiuso: sparisce dal sito entro un minuto circa.')
            }
          >
            Chiudi
          </button>
        )}
        {status !== 'draft' && !locked && (
          <button
            type="button"
            className={btnGhost}
            disabled={Boolean(busy)}
            onClick={() => void setStatus('draft', 'Tornato in bozza: non è più sul sito.')}
          >
            Torna in bozza
          </button>
        )}
        {status === 'active' && (
          <a
            href={`${config.siteUrl}/survey/${id}`}
            target="_blank"
            rel="noreferrer"
            className={btnGhost}
          >
            Apri sul sito <ExternalLink className="size-3.5" aria-hidden />
          </a>
        )}
        {survey && survey.responses > 0 && (
          <button type="button" className={btnGhost} onClick={() => void downloadCsv()}>
            <Download className="size-4" aria-hidden /> CSV
          </button>
        )}
        {survey && (
          <button
            type="button"
            className={`${btnGhost} ml-auto text-danger`}
            disabled={Boolean(busy)}
            onClick={() => void remove()}
          >
            <Trash2 className="size-4" aria-hidden /> Elimina
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 bg-ok-soft p-3 text-sm">
          {notice}
        </p>
      )}

      <div className="mt-4 flex flex-col gap-6">
        {results && results.total > 0 && survey && (
          <ResultsCard survey={survey} results={results} />
        )}

        <Card title="Testi">
          <Field label="Titolo" hint="Breve: compare nella barra in cima alle pagine.">
            <input
              className={inputCls}
              value={draft.title}
              maxLength={120}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </Field>
          <Field label="Introduzione" hint="Perché lo chiediamo (facoltativa).">
            <textarea
              className={`${inputCls} h-24 py-2`}
              value={draft.intro}
              maxLength={600}
              onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
            />
          </Field>
          <Field label="Ultimo giorno" hint="Facoltativo: dopo questo giorno sparisce dal sito.">
            <input
              type="date"
              className={`${inputCls} max-w-[200px]`}
              value={draft.endsOn}
              onChange={(e) => setDraft({ ...draft, endsOn: e.target.value })}
            />
          </Field>
        </Card>

        <Card title={`Domande (${draft.questions.length})`}>
          {locked && (
            <p className="bg-accent-soft p-3 text-sm">
              Ci sono già risposte: puoi correggere i testi delle domande, non aggiungerne,
              toglierne o cambiare tipo e scelte.
            </p>
          )}
          {draft.questions.map((q, i) => (
            <fieldset key={q.id} className="flex flex-col gap-3 border border-surface-strong p-3">
              <div className="flex flex-wrap items-end gap-3">
                <span className="pb-2 font-medium">{i + 1}.</span>
                <Field label="Tipo" className="w-[180px]">
                  <select
                    className={inputCls}
                    value={q.type}
                    disabled={locked}
                    onChange={(e) => setQ(i, { type: e.target.value as QuestionType })}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {TYPE_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </Field>
                <label className="flex h-10 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={q.required}
                    disabled={locked}
                    onChange={(e) => setQ(i, { required: e.target.checked })}
                  />
                  Obbligatoria
                </label>
                <div className="ml-auto flex gap-1">
                  <button
                    type="button"
                    className={btnGhost}
                    disabled={locked || i === 0}
                    onClick={() => move(i, -1)}
                    aria-label="Sposta su"
                  >
                    <ArrowUp className="size-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className={btnGhost}
                    disabled={locked || i === draft.questions.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label="Sposta giù"
                  >
                    <ArrowDown className="size-4" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className={btnGhost}
                    disabled={locked || draft.questions.length === 1}
                    onClick={() =>
                      setDraft({ ...draft, questions: draft.questions.filter((_, j) => j !== i) })
                    }
                    aria-label="Togli la domanda"
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </button>
                </div>
              </div>
              <Field label="Domanda">
                <input
                  className={inputCls}
                  value={q.label}
                  maxLength={300}
                  onChange={(e) => setQ(i, { label: e.target.value })}
                />
              </Field>
              <Field label="Aiuto" hint="Facoltativo: una riga sotto la domanda.">
                <input
                  className={inputCls}
                  value={q.hint}
                  maxLength={300}
                  onChange={(e) => setQ(i, { hint: e.target.value })}
                />
              </Field>
              {(q.type === 'single' || q.type === 'multiple') && (
                <Field
                  label="Risposte possibili"
                  hint={`Una per riga, da 2 a ${MAX_OPTIONS}.${locked ? '' : ' Puoi aggiungere una riga “Altro”.'}`}
                >
                  <textarea
                    className={`${inputCls} h-32 py-2`}
                    value={q.options}
                    disabled={locked}
                    onChange={(e) => setQ(i, { options: e.target.value })}
                  />
                </Field>
              )}
            </fieldset>
          ))}
          {!locked && draft.questions.length < MAX_QUESTIONS && (
            <button
              type="button"
              className={`${btnSecondary} self-start`}
              onClick={() =>
                setDraft({ ...draft, questions: [...draft.questions, blankQuestion()] })
              }
            >
              <Plus className="size-4" aria-hidden /> Aggiungi una domanda
            </button>
          )}
        </Card>
      </div>
    </main>
  );
}

function ResultsCard({ survey, results }: { survey: Survey; results: Results }) {
  const byId = useMemo(() => new Map(results.questions.map((r) => [r.id, r])), [results]);
  return (
    <Card title={`Risultati (${results.total} ${results.total === 1 ? 'risposta' : 'risposte'})`}>
      {survey.questions.map((q, i) => {
        const r = byId.get(q.id);
        if (!r) return null;
        return (
          <section key={q.id} className="flex flex-col gap-2">
            <h3 className="font-medium">
              {i + 1}. {q.label}{' '}
              <span className="text-sm font-normal text-muted">({r.answered} risposte)</span>
            </h3>
            {r.counts &&
              Object.entries(r.counts)
                .sort((a, b) => b[1] - a[1])
                .map(([option, n]) => <Bar key={option} label={option} n={n} of={r.answered} />)}
            {r.ratings && (
              <>
                <p className="text-sm">
                  Media <b>{r.average ?? '–'}</b> su 5
                </p>
                {r.ratings.map((n, v) => (
                  <Bar key={v} label={`${v + 1}`} n={n} of={r.answered} />
                ))}
              </>
            )}
            {r.texts && (
              <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
                {r.texts.length === 0 && <li className="text-sm text-muted">Nessuna risposta</li>}
                {r.texts.map((x, j) => (
                  <li key={j} className="bg-surface p-2 text-sm">
                    {x.text}
                    <span className="block text-xs text-muted">{formatStamp(x.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </Card>
  );
}

function Bar({ label, n, of }: { label: string; n: number; of: number }) {
  const pct = of ? Math.round((n / of) * 100) : 0;
  return (
    <div className="grid grid-cols-[minmax(0,12rem)_1fr_4.5rem] items-center gap-3 text-sm">
      <span className="truncate" title={label}>
        {label}
      </span>
      <span className="h-3 bg-surface-strong">
        <span className="block h-3 bg-fixed-yellow" style={{ width: `${pct}%` }} />
      </span>
      <span className="text-right tabular-nums">
        {n} · {pct}%
      </span>
    </div>
  );
}
